import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Building2, ClipboardList, Loader2, RefreshCw, Shield, Trash2, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  PRIMARY_BUYER_EMAIL,
  NORMALIZE_CONFIRM_PHRASE,
  REASSIGN_CONFIRM_PHRASE,
  PURGE_CONFIRM_PHRASE,
  previewNormalizePrimaryBuyer,
  applyNormalizePrimaryBuyer,
  diagnoseSafeCleanupClients,
  previewReassignClients,
  applyReassignClients,
  diagnoseSafeCleanupPurge,
  applySafeCleanupPurge,
  fetchSafeCleanupAudit,
} from "@/lib/safeCleanupApi";

function SectionCard({ title, icon: Icon, children, description }) {
  return (
    <section className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-start gap-2">
        {Icon ? <Icon className="w-5 h-5 text-[#0F2D5C] shrink-0 mt-0.5" /> : null}
        <div>
          <h2 className="text-sm font-semibold text-[#0F2D5C]">{title}</h2>
          {description ? <p className="text-xs text-slate-600 mt-0.5">{description}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

function JsonBlock({ data }) {
  if (!data) return null;
  return (
    <pre className="text-[10px] bg-slate-50 border rounded p-2 overflow-auto max-h-64">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

export default function PlatformSafeCleanup() {
  const [busy, setBusy] = useState("");
  const [normalizePreview, setNormalizePreview] = useState(null);
  const [normalizePhrase, setNormalizePhrase] = useState("");
  const [clientsDiag, setClientsDiag] = useState(null);
  const [reassignPreview, setReassignPreview] = useState(null);
  const [includeFiscal, setIncludeFiscal] = useState(false);
  const [reassignPhrase, setReassignPhrase] = useState("");
  const [purgeDiag, setPurgeDiag] = useState(null);
  const [purgePhrase, setPurgePhrase] = useState("");
  const [selectedPurgeEmails, setSelectedPurgeEmails] = useState([]);
  const [auditItems, setAuditItems] = useState([]);

  const loadNormalizePreview = useCallback(async () => {
    setBusy("normalize-preview");
    try {
      const data = await previewNormalizePrimaryBuyer();
      setNormalizePreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al diagnosticar comprador principal");
    } finally {
      setBusy("");
    }
  }, []);

  const loadClientsDiag = useCallback(async () => {
    setBusy("clients-diag");
    try {
      const data = await diagnoseSafeCleanupClients();
      setClientsDiag(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al diagnosticar empresas");
    } finally {
      setBusy("");
    }
  }, []);

  const loadPurgeDiag = useCallback(async () => {
    setBusy("purge-diag");
    try {
      const data = await diagnoseSafeCleanupPurge();
      setPurgeDiag(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al diagnosticar depuración");
    } finally {
      setBusy("");
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const items = await fetchSafeCleanupAudit(50);
      setAuditItems(items);
    } catch {
      /* opcional */
    }
  }, []);

  useEffect(() => {
    loadNormalizePreview();
    loadClientsDiag();
    loadAudit();
  }, [loadNormalizePreview, loadClientsDiag, loadAudit]);

  const runReassignPreview = async () => {
    setBusy("reassign-preview");
    try {
      const data = await previewReassignClients({ include_with_fiscal_history: includeFiscal });
      setReassignPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error en preview de reasignación");
    } finally {
      setBusy("");
    }
  };

  const applyNormalize = async () => {
    if (normalizePhrase.trim() !== NORMALIZE_CONFIRM_PHRASE) {
      toast.error(`Escriba la frase: ${NORMALIZE_CONFIRM_PHRASE}`);
      return;
    }
    setBusy("normalize-apply");
    try {
      await applyNormalizePrimaryBuyer({ confirm_phrase: normalizePhrase.trim() });
      toast.success("Comprador principal normalizado");
      setNormalizePhrase("");
      await loadNormalizePreview();
      await loadAudit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo aplicar");
    } finally {
      setBusy("");
    }
  };

  const applyReassign = async () => {
    if (reassignPhrase.trim() !== REASSIGN_CONFIRM_PHRASE) {
      toast.error(`Escriba la frase: ${REASSIGN_CONFIRM_PHRASE}`);
      return;
    }
    if (!reassignPreview?.can_apply) {
      toast.error("Genere un preview válido antes de aplicar.");
      return;
    }
    setBusy("reassign-apply");
    try {
      const out = await applyReassignClients({
        confirm_phrase: reassignPhrase.trim(),
        include_with_fiscal_history: includeFiscal,
      });
      toast.success(`Reasignadas ${out.applied_count} empresa(s)`);
      setReassignPhrase("");
      setReassignPreview(null);
      await loadClientsDiag();
      await loadAudit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reasignación bloqueada");
    } finally {
      setBusy("");
    }
  };

  const applyPurge = async () => {
    if (!selectedPurgeEmails.length) {
      toast.error("Seleccione al menos un registro depurable.");
      return;
    }
    if (purgePhrase.trim() !== PURGE_CONFIRM_PHRASE) {
      toast.error(`Escriba la frase: ${PURGE_CONFIRM_PHRASE}`);
      return;
    }
    setBusy("purge-apply");
    try {
      await applySafeCleanupPurge({ confirm_phrase: purgePhrase.trim(), emails: selectedPurgeEmails });
      toast.success("Depuración aplicada");
      setPurgePhrase("");
      setSelectedPurgeEmails([]);
      await loadPurgeDiag();
      await loadAudit();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Depuración fallida");
    } finally {
      setBusy("");
    }
  };

  const togglePurgeEmail = (email) => {
    setSelectedPurgeEmails((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
  };

  return (
    <div className="space-y-6 pb-8">
      <header>
        <h1 className="text-lg font-semibold text-[#0F2D5C]">Limpieza y reasignación segura</h1>
        <p className="text-xs text-slate-600 mt-1 max-w-3xl">
          Herramientas de diagnóstico y corrección comercial. No modifica robot SAT, scheduler, workers,
          FIEL, CSD, XML ni documentos CFDI. Toda acción destructiva requiere preview y confirmación explícita.
        </p>
      </header>

      <SectionCard
        title="1. Corregir comprador principal"
        icon={UserCog}
        description={`Normaliza ${PRIMARY_BUYER_EMAIL} como titular/comprador operativo (sin duplicar).`}
      >
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" disabled={!!busy} onClick={loadNormalizePreview}>
            {busy === "normalize-preview" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Diagnóstico
          </Button>
        </div>
        {normalizePreview && !normalizePreview.found && (
          <p className="text-sm text-rose-700">Usuario no encontrado — no se creará duplicado automáticamente.</p>
        )}
        {normalizePreview?.warnings?.length > 0 && (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="space-y-1">
              {normalizePreview.warnings.map((msg) => (
                <p key={msg}>{msg}</p>
              ))}
            </div>
          </div>
        )}
        {normalizePreview?.diagnosis?.license_notice &&
          normalizePreview.diagnosis.license_notice.code !== "LICENSE_DOC_WILL_CREATE_TRIAL_MINIMAL" && (
          <div className="rounded border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900">
            {normalizePreview.diagnosis.license_notice.message}
          </div>
        )}
        {normalizePreview?.diagnosis && (
          <div className="text-xs space-y-1 text-slate-700">
            <p><strong>Rol actual:</strong> {normalizePreview.diagnosis.role}</p>
            <p><strong>Diseñador:</strong> {normalizePreview.diagnosis.is_platform_designer ? "Sí" : "No"}</p>
            <p><strong>Empresas:</strong> {normalizePreview.diagnosis.clients_count}</p>
            <p><strong>Org:</strong> {normalizePreview.diagnosis.organization?.name || "—"}</p>
            <p><strong>Comprador catálogo:</strong> {normalizePreview.diagnosis.distributor_buyer?.licensee_name || "—"}</p>
          </div>
        )}
        <JsonBlock data={normalizePreview?.planned_actions} />
        {normalizePreview?.can_apply && (
          <div className="space-y-2 pt-2 border-t">
            <Label htmlFor="norm-phrase">Confirmación: {NORMALIZE_CONFIRM_PHRASE}</Label>
            <Input id="norm-phrase" value={normalizePhrase} onChange={(e) => setNormalizePhrase(e.target.value)} className="max-w-md" />
            <Button type="button" size="sm" disabled={!!busy} onClick={applyNormalize}>
              {busy === "normalize-apply" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Aplicar normalización
            </Button>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="2. Diagnóstico de empresas"
        icon={Building2}
        description="Clasifica empresas huérfanas, de prueba o ya correctas."
      >
        <Button type="button" variant="outline" size="sm" disabled={!!busy} onClick={loadClientsDiag}>
          {busy === "clients-diag" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Actualizar diagnóstico
        </Button>
        {clientsDiag?.summary && (
          <p className="text-xs text-slate-600">
            Reasignables: <strong>{clientsDiag.reassignable_count}</strong> — Resumen: {JSON.stringify(clientsDiag.summary)}
          </p>
        )}
        <div className="overflow-auto max-h-72 border rounded">
          <table className="w-full text-[10px]">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left p-2">RFC</th>
                <th className="text-left p-2">Owner</th>
                <th className="text-left p-2">Estado</th>
                <th className="text-left p-2">CFDI</th>
              </tr>
            </thead>
            <tbody>
              {(clientsDiag?.items || []).map((row) => (
                <tr key={row.client_id} className="border-t">
                  <td className="p-2 font-mono">{row.rfc}</td>
                  <td className="p-2">{row.owner_email || row.owner_id || "—"}</td>
                  <td className="p-2">{row.suggested_status}</td>
                  <td className="p-2">{row.cfdi_count ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        title="3. Reasignar empresas"
        icon={Shield}
        description={`Mueve clients.owner_id hacia ${PRIMARY_BUYER_EMAIL}. No modifica CFDI/FIEL/SAT.`}
      >
        <div className="space-y-2">
          <label className="flex items-start gap-2 text-xs">
            <Checkbox
              checked={includeFiscal}
              onCheckedChange={(v) => setIncludeFiscal(Boolean(v))}
              className="mt-0.5"
            />
            <span>
              Permitir reasignar empresas con CFDI/FIEL/SAT detectado
            </span>
          </label>
          <p className="text-[11px] text-slate-600 pl-6 leading-snug">
            Solo se actualizará <code className="text-[10px]">clients.owner_id</code> y metadatos comerciales.
            No se moverán XML, CFDI, FIEL, CSD ni historial SAT.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" disabled={!!busy} onClick={runReassignPreview}>
            Vista previa de reasignación
          </Button>
        </div>
        {reassignPreview?.blockers?.length > 0 && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 flex gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              {reassignPreview.blockers.length} bloqueo(s). Active la opción de empresas con CFDI/FIEL/SAT
              detectado o revise el listado.
            </span>
          </div>
        )}
        <JsonBlock data={reassignPreview?.planned_moves} />
        {reassignPreview?.can_apply && (
          <div className="space-y-2 pt-2 border-t">
            <Label htmlFor="reassign-phrase">Confirmación: {REASSIGN_CONFIRM_PHRASE}</Label>
            <Input id="reassign-phrase" value={reassignPhrase} onChange={(e) => setReassignPhrase(e.target.value)} className="max-w-md" />
            <Button type="button" size="sm" disabled={!!busy} onClick={applyReassign}>
              Aplicar reasignación
            </Button>
          </div>
        )}
      </SectionCard>

      <SectionCard
        title="4. Depurar registros de prueba"
        icon={Trash2}
        description="Solo usuarios/compradores sin empresas, licencias ni datos operativos."
      >
        <Button type="button" variant="outline" size="sm" disabled={!!busy} onClick={loadPurgeDiag}>
          Diagnosticar depurables
        </Button>
        <div className="space-y-1 max-h-48 overflow-auto">
          {(purgeDiag?.items || []).filter((i) => i.can_delete).map((row) => (
            <label key={`${row.type}-${row.id}`} className="flex items-start gap-2 text-xs border rounded p-2">
              <Checkbox
                checked={selectedPurgeEmails.includes(row.email)}
                onCheckedChange={() => row.email && togglePurgeEmail(row.email)}
                disabled={!row.email}
              />
              <span>
                <strong>{row.email || row.name}</strong> ({row.type}) — {row.suggested_action}
                {row.block_reason ? <span className="block text-slate-500">{row.block_reason}</span> : null}
              </span>
            </label>
          ))}
        </div>
        <div className="space-y-2 pt-2 border-t">
          <Label htmlFor="purge-phrase">Confirmación: {PURGE_CONFIRM_PHRASE}</Label>
          <Input id="purge-phrase" value={purgePhrase} onChange={(e) => setPurgePhrase(e.target.value)} className="max-w-md" />
          <Button type="button" size="sm" variant="destructive" disabled={!!busy} onClick={applyPurge}>
            Depurar seleccionados
          </Button>
        </div>
      </SectionCard>

      <SectionCard title="5. Auditoría de cambios" icon={ClipboardList}>
        <Button type="button" variant="outline" size="sm" onClick={loadAudit}>Actualizar auditoría</Button>
        <JsonBlock data={auditItems} />
      </SectionCard>
    </div>
  );
}
